import {
  useCompanyAssessmentTitleQuery,
  useEsrsAssessmentYearQuery,
  useGetActionQuery,
  useGetEsrsStandardHeaderQuery,
  useGetMetricNameQuery,
  useGetPortfolioNameQuery,
  useGetTargetQuery,
  useGroupAssessmentQuery,
} from 'models';
import { useMemo } from 'react';
import {
  BreadcrumbsList,
  ESRS_PATHS_TO_IGNORE,
  ID_REGEX,
  LinkType,
  PAI_PATHS_TO_IGNORE,
  PATHS_TO_IGNORE,
  PORTFOLIOS_PATHS_TO_IGNORE,
} from './Breadcrumbs.utils';
import { useParams } from 'react-router-dom';
import { formatDisplayQuarterYear } from 'utils/date';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment/SustainabilityAssessment.d';

const portfolioOwnerContext = {
  headers: {
    'x-hasura-role': 'portfolio-owner',
  },
};

export const useGetPathName = (
  index: number,
  path: string,
  linkType: string,
  isEditTarget: boolean,
  isDataCollection: boolean
) => {
  const {
    metricRef = '',
    cAssessmentId = '',
    portfolioId = '',
    esrsAssessmentId = '',
    gAssessmentId = '',
    standardRef = '',
    targetId = '',
    reportingYear = '',
    reportingPeriod = '',
    actionId,
  } = useParams();

  const isAssessmentId = useMemo(
    () => [cAssessmentId, gAssessmentId, portfolioId, esrsAssessmentId].includes(path),
    [cAssessmentId, portfolioId, esrsAssessmentId]
  );

  const isGroup = useMemo(() => gAssessmentId !== '', [gAssessmentId]);

  const { data: taxonomyGroupAssessment } = useGroupAssessmentQuery({
    variables: { assessmentId: gAssessmentId },
    skip: path !== gAssessmentId,
  });
  const { data: taxonomyAssessment } = useCompanyAssessmentTitleQuery({
    variables: { cAssessmentId },
    skip: path !== cAssessmentId,
  });
  const { data: esrsAssessment } = useEsrsAssessmentYearQuery({
    variables: { esrsAssessmentId },
    skip: path !== esrsAssessmentId,
  });
  const { data: portfolioName } = useGetPortfolioNameQuery({
    variables: { id: path },
    context: portfolioOwnerContext,
    skip: path !== portfolioId,
  });
  const { data: esrsTarget } = useGetTargetQuery({
    variables: { targetId: path },
    skip: path !== targetId || !isEditTarget,
  });
  const { data: esrsMetricName } = useGetMetricNameQuery({
    variables: { ref: path },
    skip: path !== metricRef || !path,
  });
  const { data: esrsAction } = useGetActionQuery({
    variables: { id: actionId },
    skip: !actionId,
  });
  const { data: esrsStandard } = useGetEsrsStandardHeaderQuery({
    variables: { reference: standardRef },
    skip: !standardRef,
  });

  const pageName = useMemo(() => {
    if (index === 0) return BreadcrumbsList[linkType].home;
    if (path === esrsAssessmentId) {
      return `${esrsAssessment?.esrsAssessment?.framework?.name} (${esrsAssessment?.esrsAssessment?.reportingYear})`;
    }
    if (path === standardRef) {
      if (isDataCollection) return `${esrsStandard?.esrsStandard?.title} setup`;
      return esrsStandard?.esrsStandard?.title;
    }
    if (path === metricRef) {
      return esrsMetricName?.EsrsMetric[0].title;
    }
    if (path === reportingYear) {
      return formatDisplayQuarterYear(reportingYear);
    }
    if (path === reportingPeriod) {
      return formatDisplayQuarterYear(reportingPeriod);
    }
    if (isAssessmentId) {
      if (linkType === LinkType.Taxonomy && isGroup) {
        return taxonomyGroupAssessment?.assessment?.name;
      }
      if (linkType === LinkType.Taxonomy && !isGroup) {
        return taxonomyAssessment?.companyAssessment?.aggregate.title;
      }
      if (linkType === LinkType.Sustainability) {
        return esrsAssessment?.esrsAssessment?.reportingYear;
      }
      if (linkType === LinkType.Pai) {
        return portfolioName?.portfolio?.name;
      }
      if (linkType === LinkType.Portfolios) {
        return portfolioName?.portfolio?.name;
      }
    }
    if (ID_REGEX.test(path) && isEditTarget) {
      return !!esrsTarget?.target?.name ? `${esrsTarget?.target?.name}` : 'Target';
    }
    if (ID_REGEX.test(path) && !!actionId) {
      return !!esrsAction?.esrs_Actions_by_pk?.title
        ? `${esrsAction?.esrs_Actions_by_pk?.title}`
        : 'Action';
    }
    return BreadcrumbsList[linkType][path] ?? path.charAt(0).toUpperCase() + path.slice(1);
  }, [
    taxonomyGroupAssessment,
    taxonomyAssessment,
    esrsAssessment,
    portfolioName,
    isAssessmentId,
    isEditTarget,
    path,
    esrsStandard
  ]);

  return pageName ?? '';
};

export const checkCurrentPath = (path: string) => ({
  isTaxonomy: path.includes('/assessments'),
  isSustainability: Object.values(FrameworkRefs).some((val) => path.includes(val)),
  isPai: path.includes('/pai/'),
  isEsrsStandard: path.includes('/standard'),
  isChart: path.includes('/chart'),
  isOnboarding: path.includes('onboarding'),
  isEditTarget: path.includes('/target') && path.includes('/edit'),
  isDataCollection: path.includes('/data-collection'),
  isEditAction: path.includes('/action-form') && path.includes('/edit'),
  isEditTaxonomy: path.includes('/assessments') && path.includes('/edit'),
  isIntegrations: path.includes('/integrations'),
});

export const isValidPath = (
  path: string,
  {
    isEsrsStandard,
    isChart,
    isEditTarget,
    isEditAction,
    isEditTaxonomy,
  }: {
    isEsrsStandard: boolean;
    isChart: boolean;
    isEditTarget: boolean;
    isEditAction: boolean;
    isEditTaxonomy: boolean;
  },
  materialStandardId: string,
  reportingUnitId: string,
  linkType: string
) => {
  const taxonomyYearRegex = /Q[1-4]-\d{4}/;
  if (path === '' || PATHS_TO_IGNORE.includes(path)) {
    return false;
  }
  if (Object.values(FrameworkRefs).some((val) => path.includes(val))) {
    return false;
  }
  if (isEsrsStandard && ESRS_PATHS_TO_IGNORE.includes(path)) {
    return false;
  }
  if (linkType === LinkType.Portfolios && PORTFOLIOS_PATHS_TO_IGNORE.includes(path)) {
    return false;
  }
  if (linkType === LinkType.Pai && PAI_PATHS_TO_IGNORE.includes(path)) {
    return false;
  }
  if (isEditTarget && path === 'target-form') {
    return false;
  }
  if (isChart && path === materialStandardId) {
    return false;
  }
  if (isEditAction && path === 'action-form') {
    return false;
  }
  if (isEditTaxonomy && taxonomyYearRegex.test(path)) {
    return false;
  }
  if (path === reportingUnitId) {
    return false;
  }
  return true;
};
